import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FileText, 
  Download, 
  Printer, 
  Share2, 
  Edit2, 
  Trash2,
  Tag,
  MapPin,
  Calendar,
  Info,
  User,
  Clock,
  Building
} from 'lucide-react';
import BackButton from '../../components/BackButton';

const DocumentDetail = () => {
  const { id } = useParams();
  const [document, setDocument] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    // Mock data - replace with actual API call
    const mockDocument = {
      id,
      name: 'Supplier Agreement 2024',
      category: 'Contracts',
      date: '2024-01-15',
      type: 'pdf',
      size: '2.5 MB',
      tags: ['supplier', 'agreement', 'legal'],
      physicalLocation: 'Cabinet A-1',
      status: 'active',
      description: 'Annual supplier agreement with vendor XYZ for 2024',
      version: '1.2',
      lastModified: '2024-02-01',
      creator: 'John Doe',
      department: 'Procurement',
      expiryDate: '2024-12-31',
      relatedDocuments: [
        { id: 2, name: 'Previous Agreement 2023' },
        { id: 3, name: 'Supplier Profile' }
      ],
      history: [
        { date: '2024-02-01', action: 'Modified', user: 'John Doe' },
        { date: '2024-01-15', action: 'Created', user: 'John Doe' }
      ]
    };

    setDocument(mockDocument);
  }, [id]);

  if (!document) return <div>Loading...</div>;

  return (
    <div className="p-6">
      <BackButton />
      
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">{document.name}</h1>
          <div className="flex items-center text-gray-600">
            <FileText className="w-5 h-5 mr-2" />
            <span className="mr-4">{document.type.toUpperCase()}</span>
            <Calendar className="w-5 h-5 mr-2" />
            <span>{document.date}</span>
          </div>
        </div>
        <div className="flex space-x-3">
          <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center">
            <Download className="w-5 h-5 mr-2" />
            Download
          </button>
          <button className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center">
            <Printer className="w-5 h-5 mr-2" />
            Print
          </button>
          <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center">
            <Share2 className="w-5 h-5 mr-2" />
            Share
          </button>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center">
            <Edit2 className="w-5 h-5 mr-2" />
            Edit
          </button>
          <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center">
            <Trash2 className="w-5 h-5 mr-2" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'details' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'history' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                History
              </button>
              <button
                onClick={() => setActiveTab('related')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'related' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Related Documents
              </button>
            </div>

            {activeTab === 'details' && (
              <div className="space-y-6">
                <div className="flex items-start">
                  <Info className="w-5 h-5 mr-3 mt-1 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium">{document.description}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Tag className="w-5 h-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Tags</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {document.tags.map(tag => (
                        <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Physical Location</p>
                    <p className="font-medium">{document.physicalLocation}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <User className="w-5 h-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Created By</p>
                    <p className="font-medium">{document.creator}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Building className="w-5 h-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-medium">{document.department}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Expiry Date</p>
                    <p className="font-medium">{document.expiryDate}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-4">
                {document.history.map((entry, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="w-16 text-sm text-gray-600">{entry.date}</div>
                    <div className="flex-1 ml-4">
                      <p className="font-medium">{entry.action}</p>
                      <p className="text-sm text-gray-600">by {entry.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'related' && (
              <div className="space-y-4">
                {document.relatedDocuments.map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/archive/${doc.id}`}
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <FileText className="w-5 h-5 mr-3 text-gray-400" />
                    <span className="font-medium">{doc.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Document Preview</h2>
          <div className="aspect-w-3 aspect-h-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <FileText className="w-24 h-24 text-gray-400" />
          </div>
          <div className="mt-4">
            <h3 className="font-semibold mb-2">Document Properties</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">File Type:</span>
                <span className="font-medium">{document.type.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Size:</span>
                <span className="font-medium">{document.size}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Version:</span>
                <span className="font-medium">{document.version}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Modified:</span>
                <span className="font-medium">{document.lastModified}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;